import React, { useEffect } from "react";
import { Helmet } from "react-helmet-async";

/**
 * SEOHead — Injects <title>, meta description, canonical, Open Graph,
 * Twitter Card tags, and JSON-LD structured data into <head>.
 *
 * Props:
 *   title, description, canonical, ogType, ogImage, ogUrl, noindex, children
 *   structuredData — optional array of JSON-LD objects. Each is rendered
 *     as its own <script type="application/ld+json"> block.
 *   lang — current page language (e.g. "en", "es"). Defaults to "en".
 *   alternates — optional { en: "/url", es: "/es/url", ... } map. Renders
 *     <link rel="alternate" hreflang="..."> for each + an x-default fallback.
 *   favicon — optional URL; when set renders <link rel="icon"> (overrides
 *     the static defaults baked into public/index.html).
 *   appleTouchIcon — optional URL for <link rel="apple-touch-icon">.
 */
export default function SEOHead({
  title,
  description,
  canonical,
  ogType = "website",
  ogImage,
  ogUrl,
  noindex = false,
  structuredData,
  lang,
  alternates,
  favicon,
  appleTouchIcon,
  children,
}) {
  const jsonLdBlocks = Array.isArray(structuredData) ? structuredData : (structuredData ? [structuredData] : []);
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const alternateEntries = alternates && typeof alternates === "object"
    ? Object.entries(alternates).filter(([, href]) => !!href)
    : [];
  const defaultHref = alternates?.en || alternates?.["x-default"] || canonical;

  // One-time cleanup: remove SEO fallbacks baked into public/index.html after
  // Helmet takes over. Every page using <SEOHead> runs this so tenant routes
  // (not wrapped by MarketingLayout) also get clean head tags. The static
  // fallbacks remain visible to pre-JS crawlers in the raw HTML — this only
  // affects the runtime DOM.
  useEffect(() => {
    document.head.querySelectorAll('[data-static-seo]').forEach(n => n.remove());
  }, []);

  return (
    <Helmet htmlAttributes={lang ? { lang } : undefined}>
      {title && <title>{title}</title>}
      {description && <meta name="description" content={description} />}
      {canonical && <link rel="canonical" href={canonical} />}
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Favicon & touch icons — runtime-overridable from Platform Admin branding */}
      {favicon && <link rel="icon" href={favicon} />}
      {appleTouchIcon && <link rel="apple-touch-icon" href={appleTouchIcon} />}

      {/* Open Graph */}
      {title && <meta property="og:title" content={title} />}
      {description && <meta property="og:description" content={description} />}
      {ogType && <meta property="og:type" content={ogType} />}
      {ogImage && <meta property="og:image" content={ogImage} />}
      {ogUrl && <meta property="og:url" content={ogUrl} />}
      {lang && <meta property="og:locale" content={lang === "es" ? "es_US" : "en_US"} />}
      <meta property="og:site_name" content={title?.split("|").pop()?.trim() || "PartyRentalSuite"} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      {title && <meta name="twitter:title" content={title} />}
      {description && <meta name="twitter:description" content={description} />}
      {ogImage && <meta name="twitter:image" content={ogImage} />}

      {/* hreflang alternates (SEO i18n) */}
      {alternateEntries.map(([lg, href]) => (
        <link
          key={`alt-${lg}`}
          rel="alternate"
          hrefLang={lg === "en" ? "en-US" : (lg === "es" ? "es-US" : lg)}
          href={href.startsWith("http") ? href : `${origin}${href}`}
        />
      ))}
      {alternateEntries.length > 0 && defaultHref && (
        <link
          rel="alternate"
          hrefLang="x-default"
          href={defaultHref.startsWith("http") ? defaultHref : `${origin}${defaultHref}`}
        />
      )}

      {/* JSON-LD structured data */}
      {jsonLdBlocks.map((block, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(block)}
        </script>
      ))}

      {children}
    </Helmet>
  );
}
